// components/SelfPaced/SelfPacedCoursesPage.js
// Matches VC_FE_REPLIT_UX_DESIGNS/SelfPacedCourses.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { PlayCircle, Plus, Search, LayoutGrid, List, SlidersHorizontal } from 'lucide-react';
import PageHeader from '../ui/PageHeader';
import { FilterModal, FilterGroup } from '../ui/FilterModal';
import CommonToaster from '../Common/CommonToaster';
import { AlertModal } from '../ui';
import { getSelfPacedCourses, deleteSelfPacedCourse } from '../../utils/selfPacedApi';
import SelfPacedCourseCard from './SelfPacedCourseCard';
import './_self-paced-pages.scss';

export default function SelfPacedCoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '' });

  // Filters
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [view, setView] = useState('grid');
  const [showFilterModal, setShowFilterModal] = useState(false);

  const activeFilterCount = [levelFilter, statusFilter, categoryFilter].filter(Boolean).length;

  const handleResetFilters = () => {
    setLevelFilter('');
    setStatusFilter('');
    setCategoryFilter('');
  };

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getSelfPacedCourses();
      const d = res.data?.data || res.data;
      setCourses(Array.isArray(d) ? d : d?.courses || []);
    } catch {
      setToast({ show: true, message: 'Failed to load courses', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSelfPacedCourse(id);
      setCourses((prev) => prev.filter((c) => c._id !== id));
      setToast({ show: true, message: 'Course deleted successfully', type: 'success' });
    } catch (err) {
      if (err?.response?.data?.errorCode === 'COURSE_HAS_ENROLLMENTS') {
        setAlertModal({
          show: true,
          title: 'Cannot Delete Course',
          message: 'Course has active enrollments. Please refund/remove enrollments first.',
        });
      } else {
        setToast({ show: true, message: 'Failed to delete course', type: 'error' });
      }
    }
  };

  // Derive unique categories from courses
  const categories = useMemo(() => {
    const cats = [...new Set(courses.map((c) => c.category).filter(Boolean))];
    return cats.sort();
  }, [courses]);

  // Filtered courses
  const filtered = useMemo(() => {
    let result = courses;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.title || '').toLowerCase().includes(q) ||
          (c.category || '').toLowerCase().includes(q)
      );
    }
    if (levelFilter) {
      result = result.filter(
        (c) => (c.level || '').toLowerCase() === levelFilter.toLowerCase()
      );
    }
    if (statusFilter) {
      result = result.filter((c) =>
        statusFilter === 'published' ? c.isPublished : !c.isPublished
      );
    }
    if (categoryFilter) {
      result = result.filter((c) => c.category === categoryFilter);
    }
    return result;
  }, [courses, search, levelFilter, statusFilter, categoryFilter]);

  const hasFilters = search || levelFilter || statusFilter || categoryFilter;

  return (
    <div className="sp-page">
      <PageHeader
        title="Self-Paced Courses"
        icon={PlayCircle}
      />

      {/* Compact Toolbar */}
      <div className="sp-toolbar">
        <button
          className="btn btn-primary sp-add-btn"
          onClick={() => navigate('/self-paced/add')}
        >
          <Plus size={16} />
          <span className="sp-add-btn-text">New Course</span>
        </button>

        <div className="sp-search">
          <Search size={16} className="sp-search-icon" />
          <input
            type="text"
            className="sp-search-input"
            placeholder="Search courses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="sp-toolbar-actions">
          <div className="sp-view-toggle">
            <button
              className={`sp-view-btn ${view === 'grid' ? 'active' : ''}`}
              onClick={() => setView('grid')}
              aria-label="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`sp-view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
              aria-label="List view"
            >
              <List size={16} />
            </button>
          </div>

          {/* Desktop inline filters */}
          {categories.length > 0 && (
            <select
              className="sp-filter-select d-none d-md-block"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          <select
            className="sp-filter-select d-none d-md-block"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <select
            className="sp-filter-select d-none d-md-block"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>

          {/* Mobile filter button */}
          <button
            className={`sp-filter-btn d-md-none${activeFilterCount > 0 ? ' sp-filter-btn--active' : ''}`}
            onClick={() => setShowFilterModal(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && (
              <span className="sp-filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="sp-loading">
          <Spinner animation="border" variant="secondary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="sp-empty">
          <div className="sp-empty-icon-wrap">
            <PlayCircle size={32} />
          </div>
          <h3 className="sp-empty-title">
            {hasFilters ? 'No matching courses' : 'No self-paced courses yet'}
          </h3>
          <p className="sp-empty-desc">
            {hasFilters
              ? 'Try adjusting your filters.'
              : 'Create your first self-paced course to get started with on-demand learning.'}
          </p>
          {!hasFilters && (
            <button className="btn btn-primary" onClick={() => navigate('/self-paced/add')}>
              <Plus size={16} /> Create Course
            </button>
          )}
        </div>
      ) : (
        <div className="sp-grid">
          {filtered.map((course) => (
            <SelfPacedCourseCard
              key={course._id}
              course={course}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Footer info */}
      {!loading && filtered.length > 0 && (
        <div className="sp-footer-info">
          Showing {filtered.length} of {courses.length} courses
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <AlertModal
        show={alertModal.show}
        onClose={() => setAlertModal({ show: false, title: '', message: '' })}
        title={alertModal.title}
        message={alertModal.message}
        variant="danger"
      />

      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        {categories.length > 0 && (
          <FilterGroup label="Category">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterGroup>
        )}
        <FilterGroup label="Level">
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </FilterGroup>
        <FilterGroup label="Status">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </FilterGroup>
      </FilterModal>
    </div>
  );
}
